/**
 * Chrome strings — the client's own affordances, not the boutique's content.
 *
 * Everything the customer reads about *garments* comes from the server as
 * localized data. What is left is the handful of words the renderer itself
 * owns ("skip", "keep 4", "move up"), which the flow document has no field
 * for. They are keyed by locale, and every lookup falls back to English, so a
 * tenant that declares a locale nobody has written a dictionary for gets a
 * whole screen in English rather than a bilingual one.
 *
 * The boutique serves Los Angeles, so the English here is American: American
 * spelling, and the vocabulary a US bridal salon actually uses at the counter
 * — sample sizes, off the rack, made to order, pulling a gown.
 */
import type { IntakeCheck } from '@fitting-room/contracts/intake'

export interface UiStrings {
  back: string
  /** The deck's own header control: back to the start of the asking. */
  editProfile: string
  /** And the way out of the asking again, once she came in through that door. */
  backToBrowsing: string
  skipToBrowse: string
  continue: string
  chooseAtLeastOne: string
  selectUpTo: (n: number) => string
  /** The skip control at the top of a step, where she can see it. */
  skipThis: string
  /** "3 of 11" — beside the progress segments. */
  stepCounter: (position: number, total: number) => string
  /** "7 more, about 2 minutes" — under the button, so the end is in sight. */
  stepsRemaining: (n: number) => string
  /** Why one typed value was refused, in her language. */
  intakeProblem: (rejection: IntakeCheck & { ok: false }, unit?: string) => string
  suggested: string
  moveUp: string
  moveDown: string
  positionAnnouncement: (label: string, position: number, total: number) => string
  reorderHint: string
  anyAmount: string
  inRange: (n: number) => string
  clear: string
  filters: string
  filterCleared: string
  ofCount: (index: number, total: number) => string
  sampleSizes: string
  /** The card's photograph, once it is a control rather than a picture. */
  enlargePhoto: (name: string) => string
  closePhoto: string
  /** The mark on the feed that opens what the gown says about itself. */
  showDetails: string
  hideDetails: string
  /**
   * The switch at the top of the gowns: four to a screen, or one. The names
   * say what each press DOES, because the glyphs only say what it looks like.
   */
  browseViews: string
  gridView: string
  singleView: string
  /** A card in the grid, by its photo: opens that gown large over the grid. */
  openGown: (name: string) => string
  emptyDeck: string
  /** The catch-all, for a relaxation this client has not been taught to name. */
  relaxedNotice: string
  /** Named, because "we widened the search" is false of a dropped filter. */
  relaxedFilters: (names: string) => string
  relaxedRangeWidened: string
  relaxedRangeDropped: string
  relaxedOrderable: string
  relaxedExhausted: string
  /** On the chip itself: still her choice, not currently in force. */
  filterSetAside: string
  /** Switching between the catalogues the boutique offers. */
  whichCatalogue: string
  keepN: (n: number) => string
  keptOf: (kept: number, total: number) => string
  nothingToShortlist: string
  /**
   * For a pick whose gown cannot be drawn — withdrawn from the catalogue
   * since she chose it, say. It replaced a raw internal id, which is what
   * these screens printed at her before there was a by-ids lookup.
   */
  gownUnavailable: string
  yourRail: string
  addNote: string
  noteLabel: string
  saveNote: string
  editableUntil: (when: string) => string
  share: string
  shareCopied: string
  submit: string
  /**
   * The last screen for someone who arrived without a link: the least we need
   * in order to keep the promise the next screen makes.
   */
  guestContactTitle: string
  guestContactBody: string
  nameLabel: string
  /** Why the send is refusing, said before she taps it rather than after. */
  contactNeeded: string
  /** Her way back to the list she just built, with nothing sent yet. */
  backToList: string
  /**
   * The claim, and only ever a claim. Nothing here looks anything up: the
   * copy must not imply that an appointment has been found, only that we will
   * go and look for it ourselves.
   */
  claimToggle: string
  claimDateLabel: string
  claimHint: string
  saveBooking: string
  /** The panel that replaces the rail once she has sent it. */
  sentTitle: string
  sentBody: string
  /** The same panel for a guest, saying which of the two things will happen. */
  sentGuestBody: string
  sentClaimBody: (day: string) => string
  /** Reveals the one field on that panel, and the field itself. */
  emailToMe: string
  emailLabel: string
  saveEmail: string
  /**
   * What we are allowed to say about it. Nothing here has been sent: the
   * address is stored and the scheduled-notification machinery is what will
   * eventually carry the list to it, so the copy promises a future and never
   * reports a past.
   */
  emailPromise: string
  emailSaved: (address: string) => string
  /** The rail is collapsed behind this, not gone — she may have come to read it. */
  showList: string
  hideList: string
  /**
   * Starting the whole thing again, from the panel that says it was sent.
   *
   * The words have to promise both halves of the rule: she gets a fresh list
   * to fill in, and the one she already sent is not thrown away the moment she
   * taps — the boutique keeps working from it until she finishes the new one.
   * A customer who reads this as "delete my list and start from nothing" is a
   * customer who will not tap it.
   */
  fillAgain: string
  fillAgainBody: string
  fillAgainFailed: string
  /**
   * The door for someone who arrived without a link, on the home page. Rendered
   * only where the boutique has asked for a public door, so a deployment
   * without one shows none of these words.
   */
  startFitting: string
  startFittingBody: string
  startFittingFailed: string
  /**
   * The home page when the boutique has NO public door. She has typed the
   * domain, or lost her link — so the useful thing to say is where a link comes
   * from, not that a feature is switched off.
   */
  homeByAppointment: string
  homeByAppointmentBody: string
  /**
   * The fitting room: the gowns laid out as a grid, her two lists, and the
   * booking. The lists' own names ("Must try", "Favorite") are the flow's and
   * arrive with it; these are only the renderer's words around them.
   */
  /** When nothing on the rail is in her size, so everything is shown instead. */
  relaxedSize: string
  /** On a list's button once it is full: "Must try is full — take one off first." */
  slotFull: (label: string) => string
  /** The bar at the foot of the gowns: the way to her lists and the booking. */
  openFittingRoom: (count: number) => string
  keepBrowsing: string
  continueBrowsing: string
  bookAppointment: string
  remove: string
  /** Moving a gown from one of her lists to the other: "Move to Favorite". */
  moveTo: (label: string) => string
  yourAppointment: string
  appointmentLength: (minutes: number) => string
  chooseDay: string
  chooseTime: string
  noTimes: string
  moreDays: string
  timeTaken: string
  bookingFailed: string
  emailOptional: string
  emailRequired: string
  emailHelp: string
  confirm: string
  confirming: string
  /** Her size screen's own control, when the flow authors no words for it. */
  notSure: string
  /** The home page, when this phone already has a fitting room on it. */
  resumeFittingTitle: string
  resumeFitting: string
  resumeFittingBody: string
  startAnother: string
  loading: string
  errorTitle: string
  errorBody: string
  retry: string
  saving: string
  saveFailed: string
  /**
   * The server refused a change because the appointment has passed. Not
   * "try once more": trying again can only fail the same way.
   */
  saveRefusedPastAppointment: string
  /** The bubble that points at the way to her fitting room, after her first pick. */
  advanceHint: string
  /** Her fitting room, the first time she reaches it with something saved. */
  roomCongratsTitle: string
  roomCongratsBody: string
  /** Fewer than `ROOM_ENOUGH` saved: how many, and a nudge to add a few. */
  roomFewPicks: (count: number) => string
  roomAddMore: string
  /** Enough saved: she can stop, or keep going. */
  roomEnoughPicks: string
  /** Booking, when it happens on Koalendar: the button and what it leads to. */
  bookOnlineNote: string
  shareTitle: string
  shareBody: string
  shareButton: string
  /** The share panel's field and its button. */
  shareLink: string
  shareMaking: string
  shareCopy: string
  shareCopiedShort: string
  shareCopyByHand: string
  close: string
  shareLinkCopied: string
  shareFailed: string
  /** The text a phone's share sheet sends with the link. */
  shareMessage: (boutique: string) => string
  /** `/v/<key>` — her fitting room as a friend sees it. */
  sharedTitle: (firstName: string | undefined) => string
  sharedBody: string
  sharedEmpty: string
  sharedStartOwn: string
  sharedStartOwnBody: string
  sharedGone: string
  /** `/reserve` — the card on file, then Koalendar. */
  reserveTitle: string
  /** `/hold` — the website's way in for a type that takes a card. */
  holdTitle: string
  holdBody: string
  reserveBody: string
  reserveType: string
  reserveName: string
  reserveEmail: string
  reservePhone: string
  reservePolicyTitle: string
  reserveAgree: string
  reserveChooseType: string
  reserveAgreeNeeded: string
  reserveCardNote: string
  reserveToCard: string
  reserveToCalendar: string
  reserveWorking: string
  reserveClosed: string
  reserveAlreadyBooked: string
  reserveFailed: string
  /** Back from Stripe: the card saved, on to the calendar. */
  reserveCardSaved: (brand: string | undefined, last4: string | undefined) => string
  reserveCardNotSaved: string
  reserveBackToStart: string
  /** `/booked` — back from Koalendar. */
  bookedTitle: string
  /** Back from Koalendar and found: who, to greet them. */
  bookedTitleFor: (firstName: string) => string
  bookedWaiting: string
  /** The appointment: "Bridal appointment · Saturday, November 20 at 11:00 AM". */
  bookedWhen: (tierLabel: string, when: string) => string
  bookedReadyBody: string
  bookedChooseGowns: string
  bookedComeBackEmailed: string
  bookedComeBack: string
  /** Not found — some other way here, too late, or the webhook is slow: the email has the link. */
  bookedEmailed: string
  bookedNoEmail: string
  /*
   * The staff console's measurements-and-note panel on an order. The rest of
   * the console still writes its words inline; these are here because the
   * panel is new, and new words go here.
   */
  /** The Edit on the Measurements card and on the Note card. */
  orderNotesEdit: string
  orderNotesEditMeasurements: string
  orderNotesEditNote: string
  orderNotesTitle: string
  orderNotesDescription: string
  orderNotesMeasurements: string
  orderNotesOtherRequest: string
  orderNotesNote: string
  orderNotesCancel: string
  orderNotesSave: string
  orderNotesSaving: string
  /*
   * "Not ordered": a dress, accessory or shoes still to order from the supplier, or
   * written as ordered with no supplier order number (`isLineNotOrdered` in
   * `domain`). The tag on the line, the list's chip, and the count on a row.
   */
  orderNotOrdered: string
  /** The tag's tooltip: how it goes away. */
  orderNotOrderedTagHint: string
  /** The chip's tooltip, as the other chips have one. */
  orderNotOrderedFilterHint: string
  /** On a row in the list: "2 not ordered". */
  orderRowNotOrdered: (count: number) => string
  /*
   * The Order progress card on an order (CMP-9 § 1): four milestones, each
   * with a check, a line saying where it stands, and what to do about it.
   * Back office: the customer is "the customer" or "they".
   */
  progressTitle: string
  /** Read aloud for a milestone's check. */
  progressStepDone: string
  progressStepNotDone: string
  progressPayment: string
  /** "$1,000.00 of $3,000.00 paid" */
  progressPaid: (paid: string, total: string) => string
  /** "$2,000.00 due by Feb 18, 2027" */
  progressBalanceDueBy: (amount: string, day: string) => string
  progressBalanceDue: (amount: string) => string
  progressPaidInFull: string
  progressRecordPayment: string
  /** A canceled order only takes a refund. */
  progressRecordRefund: string
  progressInvoice: string
  /** "Last emailed Nov 2, 2026" */
  progressInvoiceSentOn: (day: string) => string
  /** "Last emailed Nov 2, 2026, to 2 addresses" */
  progressInvoiceSentTo: (day: string, count: number) => string
  progressInvoiceNotSent: string
  progressEmailInvoice: string
  progressDresses: string
  /** "1 of 2 told or picked up" */
  progressDressesSummary: (done: number, total: number) => string
  progressNoDresses: string
  progressShowPieces: string
  progressHidePieces: string
  progressStepOrdered: string
  progressStepShipped: string
  progressStepArrived: string
  progressStepNotified: string
  progressStepPickedUp: string
  /** An off-the-rack piece has no supplier steps; only its pickup shows. */
  progressOffTheRack: string
  progressMarkArrived: string
  progressEmailCustomer: string
  progressMarkNotified: string
  progressUndoNotified: string
  /** "Told by phone" */
  progressToldBy: (how: string) => string
  progressViaEmail: string
  progressViaPhone: string
  progressViaText: string
  progressViaInPerson: string
  progressPickup: string
  progressPickedUpOn: (day: string) => string
  progressExpectedPickup: (day: string) => string
  progressCancelOrder: string
  progressRestoreOrder: string
  progressEmailChecking: string
  progressEmailNotConfigured: string
  progressEmailStatusFailed: string
  /* The arrival email's sheet. */
  arrivalSheetTitle: string
  arrivalSheetDescription: (piece: string) => string
  arrivalTo: string
  arrivalOnFile: string
  arrivalAddAddress: string
  arrivalRemoveAddress: string
  arrivalNote: string
  arrivalNoteHint: string
  arrivalPreview: string
  arrivalPreviewLoading: string
  arrivalPreviewFailed: string
  arrivalSend: string
  arrivalSending: string
  arrivalNotSent: string
  /* The Mark as notified sheet. */
  markNotifiedDescription: (piece: string) => string
  markNotifiedHow: string
  markNotifiedSaving: string
  progressCancel: string
  /*
   * The Order block in the appointment drawer: what the orders on it come to,
   * tax included. A canceled order is shown as Canceled and left out of it.
   */
  visitOrdersTotal: string
  visitOrdersCanceledNotCounted: string
  /*
   * The marketing partner's console (`role: marketer`): one read-only list of
   * appointments, the customer masked by the server before it gets here.
   */
  marketerRole: string
  marketingTitle: string
  marketingDescription: string
  marketingMonth: string
  marketingLoading: string
  marketingFailed: string
  marketingEmpty: string
  marketingColWhen: string
  marketingColCustomer: string
  marketingColContact: string
  marketingColType: string
  marketingColSource: string
  marketingColStylist: string
  marketingColWedding: string
  marketingColCameIn: string
  marketingColSold: string
  marketingColOrders: string
  /** The Came in column, by the appointment's status. */
  marketingCameIn: (status: 'booked' | 'completed' | 'cancelled' | 'no_show') => string
  marketingSoldYes: string
  marketingSoldNo: string
  marketingNone: string
  marketingNotHereTitle: string
  marketingNotHereBody: string
  marketingBack: string
  /** A pickup appointment: booked, and nothing to open. */
  bookedPickupBody: string
}

/** Two lists with this many between them is plenty for a stylist to pull from. */
export const ROOM_ENOUGH = 5

/** Stripe's brand words as a card says them: `visa` is Visa, `amex` American Express. */
function cardBrand(brand: string): string {
  const named: Record<string, string> = { amex: 'American Express', diners: 'Diners Club', mastercard: 'Mastercard', unionpay: 'UnionPay' }
  return named[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1)
}

const en: UiStrings = {
  back: 'Back',
  editProfile: 'Edit profile',
  backToBrowsing: 'Back to browsing',
  skipToBrowse: 'Skip the questions, show me everything',
  continue: 'Continue',
  chooseAtLeastOne: 'Choose one to continue, or skip',
  selectUpTo: (n) => `Choose up to ${n}`,
  skipThis: 'Skip this',
  stepCounter: (position, total) => `${position} of ${total}`,
  stepsRemaining: (n) =>
    n === 0 ? 'Last one' : n === 1 ? 'One more, about ten seconds' : `${n} more, about ${Math.max(1, Math.round(n / 4))} minute${n > 5 ? 's' : ''}`,
  intakeProblem: (rejection, unit) => {
    switch (rejection.reason) {
      case 'not_a_number':
        return 'Numbers only, please.'
      case 'below_min':
        return `That looks low — ${rejection.min}${unit ? ` ${unit}` : ''} or more.`
      case 'above_max':
        return `That looks high — ${rejection.max}${unit ? ` ${unit}` : ''} or less.`
      case 'not_a_date':
        return 'That’s not a date we recognize.'
      case 'not_an_email':
        return 'That doesn’t look like an email address.'
      case 'too_long':
        return `A little shorter, please — up to ${rejection.maxLength} characters.`
      default:
        return 'That can’t be blank.'
    }
  },
  suggested: 'Suggested',
  moveUp: 'Move up',
  moveDown: 'Move down',
  positionAnnouncement: (label, position, total) => `${label}, position ${position} of ${total}`,
  reorderHint: 'Drag to reorder, or use the arrow buttons.',
  anyAmount: 'No limit',
  inRange: (n) => `${n} in range`,
  clear: 'Clear',
  filters: 'Filters',
  filterCleared: 'Filters cleared',
  ofCount: (index, total) => `${index} of ${total}`,
  sampleSizes: 'Sample sizes',
  enlargePhoto: (name) => `Enlarge the photo of ${name}`,
  closePhoto: 'Close',
  showDetails: 'Details',
  hideDetails: 'Hide details',
  browseViews: 'How the gowns are shown',
  gridView: 'Show as a grid',
  singleView: 'Show one at a time',
  openGown: (name) => `Open ${name}`,
  emptyDeck: 'Nothing matches those filters yet. Loosen one and they’ll come back.',
  relaxedNotice: 'We widened the search so this wouldn’t come back empty.',
  relaxedFilters: (names) =>
    `Nothing in the collection matches ${names} along with everything else you asked for, so we’re showing a little more for now.`,
  relaxedRangeWidened: 'We stretched the budget a little so this wouldn’t come back empty.',
  relaxedRangeDropped: 'Nothing matched that budget, so we’re ignoring it for now.',
  relaxedOrderable: 'Some of these aren’t in the store to try on today — they can be made to order.',
  relaxedExhausted: 'Nothing in the collection matches this yet.',
  filterSetAside: 'not in effect',
  whichCatalogue: 'Which gowns',
  keepN: (n) => `Keep ${n}`,
  keptOf: (kept, total) => `${kept} of ${total} kept`,
  nothingToShortlist: 'Nothing here yet.',
  gownUnavailable: 'This gown is no longer listed',
  yourRail: 'Your lineup',
  addNote: 'Add a note',
  noteLabel: 'Note for your stylist',
  saveNote: 'Save note',
  editableUntil: (when) => `You can change this until ${when}.`,
  share: 'Share this list',
  shareCopied: 'Link copied',
  submit: 'Send to the boutique',
  guestContactTitle: 'Who should we get in touch with?',
  guestContactBody: 'Your list is safe. We just need a name and a way to reach you.',
  nameLabel: 'Your name',
  contactNeeded: 'We need your name and an email address to get back to you.',
  backToList: 'Back to my list',
  claimToggle: 'I already have an appointment',
  claimDateLabel: 'Your appointment date',
  claimHint: 'Your stylist will match it to your appointment.',
  saveBooking: 'Save this',
  sentTitle: 'Congratulations — that’s sent.',
  sentBody: 'Your stylist has your list, and will have it at your appointment.',
  sentGuestBody: 'Someone will reach out to set up an appointment, and your list will be waiting.',
  sentClaimBody: (day) => `Your stylist will match your list to your appointment on ${day}.`,
  emailToMe: 'Send the list to me',
  emailLabel: 'Your email address',
  saveEmail: 'Save this address',
  emailPromise: 'We’ll send your list to this address.',
  emailSaved: (address) => `Saved. We’ll send your list to ${address}.`,
  showList: 'Show my list',
  hideList: 'Hide my list',
  fillAgain: 'Fill it in again',
  fillAgainBody: 'Changed your mind? Start a new list. This one stays with your stylist until you send the new one.',
  fillAgainFailed: 'That didn’t open. Try once more.',
  startFitting: 'Start a new fitting',
  // The order is the promise: gowns first, booking afterwards. Saying so here
  // is what stops the button reading like the start of a booking form.
  startFittingBody: 'See the gowns, save the ones you love, and book your appointment when you’re ready.',
  startFittingFailed: 'That didn’t open. Try once more.',
  homeByAppointment: 'By appointment',
  homeByAppointmentBody:
    'Your fitting room opens from the link we sent you when you booked. If you can’t find it, text the boutique and we’ll send it again.',
  relaxedSize: 'Nothing off the rack is in your size right now, so here’s everything we have.',
  slotFull: (label) => `${label} is full — take one off to add another.`,
  openFittingRoom: (count) => (count === 0 ? 'Your fitting room' : `Your fitting room · ${count}`),
  keepBrowsing: 'Keep browsing',
  continueBrowsing: 'Continue browsing',
  bookAppointment: 'Book my appointment',
  remove: 'Remove',
  moveTo: (label) => `Move to ${label}`,
  yourAppointment: 'Your appointment',
  appointmentLength: (minutes) => `${minutes} minutes`,
  chooseDay: 'Choose a day',
  chooseTime: 'Choose a time',
  noTimes: 'There are no open times online right now. Text the boutique and we’ll find you one.',
  moreDays: 'More days',
  timeTaken: 'Someone just booked that time. Choose another and we’ll hold it for you.',
  bookingFailed: 'That didn’t book. Try once more.',
  emailOptional: 'Email (optional)',
  emailRequired: 'Email',
  emailHelp: 'We’ll send your confirmation here.',
  confirm: 'Confirm',
  confirming: 'Booking',
  notSure: 'I’m not sure',
  resumeFittingTitle: 'Welcome back',
  resumeFitting: 'Open my fitting room',
  resumeFittingBody: 'Pick up where you left off — your favorites and must-tries are saved.',
  startAnother: 'Start a new one',
  loading: 'Opening the room',
  errorTitle: 'This link isn’t open.',
  errorBody: 'It may have expired, or the appointment may have passed. Your boutique can send a new one.',
  retry: 'Try again',
  saving: 'Saving',
  saveFailed: 'That didn’t save. Try once more.',
  saveRefusedPastAppointment: 'Your appointment has passed, so this list can’t be changed anymore.',
  advanceHint: 'Done choosing? Tap here to see your fitting room.',
  roomCongratsTitle: 'Congratulations!',
  roomCongratsBody: 'Every gown you saved is here, and your stylist will see them too.',
  roomFewPicks: (count) =>
    `You’ve saved ${count} ${count === 1 ? 'gown' : 'gowns'}. ${ROOM_ENOUGH} or more gives your stylist plenty to pull — want to add a few more?`,
  roomAddMore: 'Add more gowns',
  roomEnoughPicks: 'That’s a great lineup. You can keep browsing and change your picks anytime before your appointment.',
  bookOnlineNote: 'You’ll add a card to reserve your appointment, then pick a time.',
  shareTitle: 'Share with a friend',
  shareBody: 'Send your mom, your maid of honor or your best friend a view-only link to your picks.',
  shareButton: 'Share my fitting room',
  shareLink: 'View-only link',
  shareMaking: 'Making your link…',
  shareCopy: 'Copy',
  shareCopiedShort: 'Copied',
  shareCopyByHand: 'This browser won’t copy it for you. The link is selected — copy it from the field.',
  close: 'Close',
  shareLinkCopied: 'Link copied. Paste it into a text or an email.',
  shareFailed: 'That didn’t work. Try once more.',
  shareMessage: (boutique) => `Here are the gowns I saved at ${boutique}.`,
  sharedTitle: (firstName) => (firstName ? `${firstName}’s fitting room` : 'A fitting room'),
  sharedBody: 'A view-only list of the gowns they saved.',
  sharedEmpty: 'Nothing saved here yet.',
  sharedStartOwn: 'Start your own fitting room',
  sharedStartOwnBody: 'Browse the gowns and save the ones you love — it’s yours, not theirs.',
  sharedGone: 'This link isn’t valid anymore. Ask for a new one.',
  reserveTitle: 'Book your appointment',
  holdTitle: 'Reserve your appointment',
  holdBody: 'First, a card to hold your appointment. Then you’ll choose a time.',
  reserveBody: 'Tell us who you are, then choose a time that works for you.',
  reserveType: 'Appointment',
  reserveName: 'Name',
  reserveEmail: 'Email',
  reservePhone: 'Phone (optional)',
  reservePolicyTitle: 'Cancellation and no-show policy',
  reserveAgree: 'I agree to the cancellation and no-show policy.',
  reserveChooseType: 'Choose an appointment.',
  reserveAgreeNeeded: 'Check the box to agree to the cancellation and no-show policy before adding a card.',
  reserveCardNote: 'You’ll add your card on Stripe’s secure page. We never see or store your card number.',
  reserveToCard: 'Add a card and continue',
  reserveToCalendar: 'Choose a time',
  reserveWorking: 'One moment',
  reserveClosed: 'Online booking isn’t open yet. Call or text the boutique, and we’ll book it for you.',
  reserveAlreadyBooked: 'Your fitting room already has an appointment.',
  reserveFailed: 'That didn’t go through. Try again in a moment.',
  reserveCardSaved: (brand, last4) =>
    brand && last4 ? `Your ${cardBrand(String(brand))} ending in ${last4} is on file. Taking you to the calendar…` : 'Your card is on file. Taking you to the calendar…',
  reserveCardNotSaved: 'Your card wasn’t saved, so nothing is booked yet.',
  reserveBackToStart: 'Try again',
  bookedTitle: 'You’re booked',
  bookedTitleFor: (firstName) => `You’re booked, ${firstName}`,
  bookedWaiting: 'Getting your fitting room ready…',
  bookedWhen: (tierLabel, when) => `${tierLabel} · ${when}`,
  bookedReadyBody: 'Your fitting room is open. Choose the gowns you’d like to try, and we’ll have them pulled and waiting when you arrive.',
  bookedChooseGowns: 'Choose my gowns',
  bookedComeBackEmailed: 'No rush: we’ve emailed you the link, so you can come back anytime before your appointment.',
  bookedComeBack: 'No rush: you can come back anytime before your appointment.',
  bookedEmailed:
    'We’ve emailed you a link to your fitting room. Open it anytime before your appointment to choose the gowns you’d like to try, and we’ll have them pulled and waiting.',
  bookedNoEmail: 'Your confirmation is on its way to your inbox. We look forward to seeing you.',
  orderNotesEdit: 'Edit',
  orderNotesEditMeasurements: 'Edit measurements',
  orderNotesEditNote: 'Edit note',
  orderNotesTitle: 'Measurements and notes',
  orderNotesDescription: 'Every change goes on the order’s history, with who made it.',
  orderNotesMeasurements: 'Measurements',
  orderNotesOtherRequest: 'Other request',
  orderNotesNote: 'Note',
  orderNotesCancel: 'Cancel',
  orderNotesSave: 'Save',
  orderNotesSaving: 'Saving…',
  orderNotOrdered: 'Not ordered',
  orderNotOrderedTagHint: 'No supplier order number yet. Add it under Supplier & ETA, or link the line to Jovita.',
  orderNotOrderedFilterHint: 'Open, and a dress, accessory or pair of shoes from the supplier has no supplier order number yet',
  orderRowNotOrdered: (count) => `${count} not ordered`,
  progressTitle: 'Order progress',
  progressStepDone: 'Done',
  progressStepNotDone: 'Not done',
  progressPayment: 'Payment',
  progressPaid: (paid, total) => `${paid} of ${total} paid`,
  progressBalanceDueBy: (amount, day) => `${amount} due by ${day}`,
  progressBalanceDue: (amount) => `${amount} due`,
  progressPaidInFull: 'Paid in full',
  progressRecordPayment: 'Record payment',
  progressRecordRefund: 'Record refund',
  progressInvoice: 'Invoice',
  progressInvoiceSentOn: (day) => `Last emailed ${day}`,
  progressInvoiceSentTo: (day, count) => `Last emailed ${day}, to ${count} ${count === 1 ? 'address' : 'addresses'}`,
  progressInvoiceNotSent: 'Not emailed yet',
  progressEmailInvoice: 'Email invoice',
  progressDresses: 'Dresses',
  progressDressesSummary: (done, total) => `${done} of ${total} told or picked up`,
  progressNoDresses: 'Nothing to order or pick up',
  progressShowPieces: 'Show each piece',
  progressHidePieces: 'Hide pieces',
  progressStepOrdered: 'Ordered',
  progressStepShipped: 'Shipped',
  progressStepArrived: 'Arrived',
  progressStepNotified: 'Customer notified',
  progressStepPickedUp: 'Picked up',
  progressOffTheRack: 'Off the rack',
  progressMarkArrived: 'Mark arrived',
  progressEmailCustomer: 'Email the customer',
  progressMarkNotified: 'Mark as notified',
  progressUndoNotified: 'Undo',
  progressToldBy: (how) => `Told by ${how}`,
  progressViaEmail: 'email',
  progressViaPhone: 'Phone',
  progressViaText: 'Text',
  progressViaInPerson: 'In person',
  progressPickup: 'Pickup',
  progressPickedUpOn: (day) => `Picked up ${day}`,
  progressExpectedPickup: (day) => `Expected ${day}`,
  progressCancelOrder: 'Cancel order',
  progressRestoreOrder: 'Restore order',
  progressEmailChecking: 'Checking email…',
  progressEmailNotConfigured:
    "Email isn't set up for this boutique, so the console can't send it. Call or text the customer, then use Mark as notified.",
  progressEmailStatusFailed: "Couldn't check whether email is set up. Reload to try again, or call the customer and mark it by hand.",
  arrivalSheetTitle: 'Email the customer',
  arrivalSheetDescription: (piece) => `Let the customer know ${piece} has arrived. It's marked as notified once it's sent.`,
  arrivalTo: 'To',
  arrivalOnFile: 'On file:',
  arrivalAddAddress: 'Add an address',
  arrivalRemoveAddress: 'Remove this address',
  arrivalNote: 'Note (optional)',
  arrivalNoteHint: "Goes under the piece, in your words. The preview updates when you're done typing.",
  arrivalPreview: "What they'll get",
  arrivalPreviewLoading: 'Loading the preview…',
  arrivalPreviewFailed: "Couldn't load the preview. You can still send; it's the same email.",
  arrivalSend: 'Send',
  arrivalSending: 'Sending…',
  arrivalNotSent:
    "Email isn't set up, so it wasn't sent and the piece isn't marked. Call or text the customer, then use Mark as notified.",
  markNotifiedDescription: (piece) => `How did you let the customer know ${piece} has arrived?`,
  markNotifiedHow: 'How',
  markNotifiedSaving: 'Saving…',
  progressCancel: 'Cancel',
  visitOrdersTotal: 'Total',
  visitOrdersCanceledNotCounted: 'Canceled orders aren’t counted.',
  marketerRole: 'Marketer',
  marketingTitle: 'Appointments',
  marketingDescription:
    'Every appointment, whether the customer came in, and whether they bought. Names, emails and phone numbers are shortened for privacy.',
  marketingMonth: 'Month',
  marketingLoading: 'Loading appointments…',
  marketingFailed: 'Couldn’t load the appointments. Try again in a moment.',
  marketingEmpty: 'No appointments this month.',
  marketingColWhen: 'Date & time',
  marketingColCustomer: 'Customer',
  marketingColContact: 'Contact',
  marketingColType: 'Appointment',
  marketingColSource: 'Source',
  marketingColStylist: 'Bridal stylist',
  marketingColWedding: 'Wedding date',
  marketingColCameIn: 'Came in',
  marketingColSold: 'Sold',
  marketingColOrders: 'Orders',
  marketingCameIn: (status) =>
    status === 'completed' ? 'Yes' : status === 'cancelled' ? 'Canceled' : status === 'no_show' ? 'No-show' : 'Not yet',
  marketingSoldYes: 'Yes',
  marketingSoldNo: 'No',
  marketingNone: '—',
  marketingNotHereTitle: 'This page isn’t available',
  marketingNotHereBody: 'Your access covers the Appointments list only. Ask the boutique owner if you need something else.',
  marketingBack: 'Back to appointments',
  bookedPickupBody: 'Your confirmation is on its way to your inbox. Your gown will be ready for you, and we can’t wait to see you in it.',
}

const dictionaries: Record<string, UiStrings> = { en }

export function uiStrings(locale: string): UiStrings {
  return dictionaries[locale] ?? dictionaries[locale.split('-')[0] ?? ''] ?? en
}
